import { inject, Injectable, signal, WritableSignal } from '@angular/core';
import { environment } from '../../environments/environment';
import { HttpClient } from '@angular/common/http';
import { IsTheme } from '../interfaces/is-theme';
import { catchError, map, Observable } from 'rxjs';
import { defaultTheme } from '../../defaults/default-theme';

@Injectable({
	providedIn: 'root',
})
export class ThemeService {
	private readonly api: string = environment.api;
	private readonly http: HttpClient = inject(HttpClient);

	public theme: WritableSignal<IsTheme | undefined> = signal<IsTheme | undefined>(undefined);
	public themeInstalled = false;

	public getTheme(): Observable<IsTheme> {
		// Check if theme is already in memory
		if (this.theme()) {
			// Return theme from memory
			return new Observable<IsTheme>(observer => {
				observer.next(this.theme()!);
				observer.complete();
			});
		} else {
			// Fetch theme
			return this.fetchThemeByHostname().pipe(
				map((theme: IsTheme) => {
					// If backgroundUrl is not provided by API, use default theme's backgroundUrl
					if (!theme.backgroundUrl && defaultTheme.backgroundUrl) {
						theme.backgroundUrl = defaultTheme.backgroundUrl;
					}
					// Set theme in memory
					this.theme.set(theme);
					// Return theme
					return theme;
				}),
				catchError(({ error }) => {
					console.log(error.message);
					// Set default theme in memory
					this.theme.set(defaultTheme);
					// Return default theme if theme is not found
					return this.getDefaultTheme();
				})
			);
		}
	}

	public getDefaultTheme(): Observable<IsTheme> {
		return new Observable(observer => {
			observer.next(defaultTheme);
			observer.complete();
		});
	}

	public installTheme(theme: IsTheme): void {
		// Install theme in DOM
		document.documentElement.style.setProperty('--color-primary', theme.primaryColor);
		document.documentElement.style.setProperty('--color-secondary', theme.secondaryColor);
		document.documentElement.style.setProperty('--color-tertiary', theme.tertiaryColor);
		document.documentElement.style.setProperty('--color-header', theme.webHeaderColor);
		if (theme.backgroundUrl) {
			document.documentElement.style.setProperty('--background-url', `url(${theme.backgroundUrl})`);
		} else {
			document.documentElement.style.removeProperty('--background-url');
		}
		// Set theme installed flag
		this.themeInstalled = true;
	}

	private fetchThemeByHostname(): Observable<IsTheme> {
		return this.http.get<IsTheme>(`${this.api}/theme/hostname`);
	}
}
